import ChatBubble from "@mui/icons-material/ChatBubble";
import FolderIcon from "@mui/icons-material/Folder";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Badge from "@mui/material/Badge";
import { GridColumns } from "@mui/x-data-grid-pro";
import { createContext, useContext } from "react";
import { ExtendedRepositoryObject, ValidityStatus } from "../backend/types";
import QcActionFlag from "../components/abstractComponents/qcActionFlag";
import FileIcon from "../components/abstractComponents/fileIcon";
import { formatFileSize, getKeyByValue } from "../helpers/stringHelper";
import {
  GridColDef,
  getGridStringOperators,
  getGridDateOperators,
  getGridNumericOperators,
  GridFilterItem,
  GridFilterInputValue
} from "@mui/x-data-grid-pro";
import { Button } from "react-bootstrap";

/****MENU CONTEXT****/
export function dragThis(e: any) {
  e.dataTransfer.setData("versionId", e.target.getAttribute("data-versionid"));
  e.dataTransfer.setData(
    "absolutePath",
    e.target.getAttribute("data-absolutepath")
  );
  e.dataTransfer.setData(
    "overallValidityStatus",
    e.target.getAttribute("data-overallvaliditystatus")
  );
}

export const MenuContext = createContext<{
  handleClickFolder: (
    event: React.MouseEvent<HTMLButtonElement>,
    absolutePath: string
  ) => void;
  handleClickFile: (
    event: React.MouseEvent<HTMLButtonElement>,
    absolutePath: string
  ) => void;
  handleClickComment: (
    event: React.MouseEvent<HTMLButtonElement>,
    comments: any[]
  ) => void;
  onNodeSelect: Function;
}>({
  handleClickFolder: () => {},
  handleClickFile: () => {},
  handleClickComment: () => {},
  onNodeSelect: () => {}
});

export const IdCell = (props: { row: ExtendedRepositoryObject }) => {
  const { handleClickFolder } = useContext(MenuContext);
  const { handleClickFile } = useContext(MenuContext);
  const { handleClickComment } = useContext(MenuContext);

  return (
    <div
      key={props.row.absolutePath + props.row.versionId}
      style={{ width: "100%" }}
    >
      {props.row.absolutePath.endsWith("/") ? (
        <Button
          className="float-end inner_menu_button"
          onClick={(e) => handleClickFolder(e, props.row.absolutePath)}
          variant="link"
        >
          <MoreVertIcon className="dot_menu_browse_repository grey_color my-2" />
        </Button>
      ) : (
        <>
          <Button
            className="float-end inner_menu_button"
            onClick={(e) => handleClickFile(e, props.row.absolutePath)}
            variant="link"
          >
            <MoreVertIcon className="dot_menu_browse_repository grey_color" />
          </Button>{" "}
          {props.row.comments && props.row.comments.length > 0 && (
            <Button
              className="float-end fs-7 mt-2 p-0"
              onClick={(e) => handleClickComment(e, props.row.comments!)}
              variant="link"
            >
              <Badge
                className="comments_icon"
                badgeContent={props.row.comments.length}
                color="info"
              >
                <ChatBubble className="table_view_comment_menu" />
              </Badge>
            </Button>
          )}
        </>
      )}
      <br style={{ clear: "both" }} />
    </div>
  );
};

export const NameCell = (props: {
  row: ExtendedRepositoryObject;
  allowElementExplorer: Boolean;
  refetch: Function;
  hideContextMenu?: Boolean;
  hideQcFlagMenu?: Boolean;
  consolidateEditActionCommands?: {
    setCurrentSelectedVersionId: Function;
    handleOpenEditAction: Function;
  };
}) => {
  const { onNodeSelect } = useContext(MenuContext);
  const { handleClickFile } = useContext(MenuContext);

  const clickElement = (event: any) => {
    if (
      (!props.allowElementExplorer && props.row.absolutePath.endsWith("/")) ||
      props.allowElementExplorer
    ) {
      const target = event.target as HTMLElement;
      const parent = target.parentNode as HTMLElement;
      const hyperParent = parent.parentNode as HTMLElement;
      const ultraParent = hyperParent.parentNode as HTMLElement;

      if (
        !hyperParent!.classList.contains("qc_badge") &&
        !target!.classList.contains("qc_badge") &&
        !parent!.classList.contains("qc_badge") &&
        !ultraParent!.classList.contains("qc_badge")
      ) {
        onNodeSelect({
          path: props.row.absolutePath,
          versionId: props.row.versionId,
          revision: props.row.revision,
          repoId: props.row.repository.id
        });
      }
    }
  };

  return (
    <div
      key={props.row.absolutePath + props.row.versionId}
      style={{ width: "100%" }}
    >
      <div
        role="button"
        onClick={(event) =>
          ((props.allowElementExplorer &&
            props.row.absolutePath.endsWith("/")) ||
            (props.allowElementExplorer &&
              !props.row.absolutePath.endsWith("/")) ||
            props.row.absolutePath.endsWith("/")) &&
          clickElement(event)
        }
        className="button_without_text_transform float-start"
      >
        {props.row.absolutePath?.endsWith("/") ? (
          <>
            <FolderIcon
              className="grey_color folder_icon"
              style={{ fontSize: 40 }}
            />
            <span className="folder_name_table_view_span">
              {props.row.name}
            </span>
          </>
        ) : (
          <div
            draggable="true"
            data-revision={props.row.revision}
            data-versionid={props.row.versionId}
            data-absolutepath={props.row.absolutePath}
            data-overallvaliditystatus={
              props.row.validity?.overallValidityStatus
            }
            onDragStart={dragThis}
          >
            {props.hideQcFlagMenu ? (
              <FileIcon
                fileName={props.row.name}
                absolutePath={props.row.absolutePath}
                overallValidityStatus={
                  props.row.validity?.overallValidityStatus ?? null
                }
                fontSize={"large"}
                className={"description_icon"}
              />
            ) : (
              <QcActionFlag
                consolidateEditActionCommands={
                  props.consolidateEditActionCommands
                }
                handleClickFile={handleClickFile}
                absolutePath={props.row.absolutePath}
                refetch={props.refetch}
                validity={props.row.validity}
                versionId={String(props.row.versionId)}
              >
                <FileIcon
                  fileName={props.row.name}
                  absolutePath={props.row.absolutePath}
                  overallValidityStatus={
                    props.row.validity?.overallValidityStatus ?? null
                  }
                  fontSize={"large"}
                  className={"description_icon"}
                />
              </QcActionFlag>
            )}

            <span className="file_name_table_view_span">{props.row.name}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export const columnsRender = (
  refetch: Function = () => {},
  hideContextMenu: Boolean = false,
  allowElementExplorer: Boolean = true,
  hideQcFlagMenu: Boolean = false,
  consolidateEditActionCommands?: {
    setCurrentSelectedVersionId: Function;
    handleOpenEditAction: Function;
  }
): GridColumns<any> => {
  const idCellColumn = {
    field: "Context Menu",
    headerName: "",
    width: 100,
    filterable: false,
    sortable: false,
    hideable: false,
    renderCell: (params: any) => <IdCell row={params.row} />
  };

  return [
    {
      field: "name",
      headerName: "Name",
      filterOperators: customFilters(true),
      flex: 4,
      renderCell: (params: any) => (
        <NameCell
          row={params.row}
          allowElementExplorer={allowElementExplorer}
          refetch={refetch}
          hideContextMenu={hideContextMenu}
          hideQcFlagMenu={hideQcFlagMenu}
          consolidateEditActionCommands={consolidateEditActionCommands}
        />
      ),
      valueGetter: (params: any) => params.row.name
    },
    {
      field: "systemValidity",
      headerName: "System File Validity",
      filterOperators: customFilters(true),
      flex: 2,
      hide: true,
      sortable: false, // difficult to do both client and server sort and filtering on specific columns
      filterable: false,
      valueGetter: (params: any) =>
        getKeyByValue(ValidityStatus, params.row.validity?.systemValidityStatus)
    },
    {
      field: "userValidity",
      headerName: "User File Validity",
      filterOperators: customFilters(true),
      flex: 2,
      hide: true,
      sortable: false, // difficult to do both client and server sort and filtering on specific columns
      filterable: false,
      valueGetter: (params: any) =>
        getKeyByValue(ValidityStatus, params.row.validity?.userValidityStatus)
    },
    ...(hideContextMenu ? [] : [idCellColumn]),
    {
      field: "size",
      headerName: "Size",
      filterOperators: customFilters(true),
      flex: 1,
      valueGetter: (params: any) => params.row.size,
      renderCell: (params: any) =>
        params.row.absolutePath.endsWith("/")
          ? ""
          : formatFileSize(params.row.size),
      sortComparator: (v1: any, v2: any, param1: any, param2: any) =>
        (param1.api.getCellValue(param1.id, "size") as number) -
        (param2.api.getCellValue(param2.id, "size") as number)
    },
    {
      field: "createdAt",
      type: "date",
      headerName: "Last Updated Date",
      filterOperators: customDateFilters,
      flex: 2
    },
    {
      field: "createdBy",
      headerName: "Last Updated By",
      filterOperators: customFilters(true),
      flex: 2,
      renderCell: (params: any) => params.row.createdBy
    }
  ];
};

export const customFilters = (useIsAnyOf: Boolean = false) => {
  const baseFilters = useIsAnyOf
    ? getGridStringOperators()
    : getGridStringOperators().filter(
        (filter) => filter.value !== "isAnyOf" // very difficult to get through GrqphQL
      );

  return [
    ...baseFilters,
    {
      label: "does not contain",
      value: "noContain",
      getApplyFilterFn: (filterItem: GridFilterItem, column: GridColDef) => {
        if (
          !filterItem.columnField ||
          !filterItem.value ||
          !filterItem.operatorValue
        ) {
          return null;
        }

        const filterRegex = new RegExp(filterItem.value, "i");
        return (params: any): boolean => {
          const rowValue = column.valueGetter
            ? column.valueGetter(params)
            : params.value;
          return !filterRegex.test(rowValue?.toString() || "");
        };
      },
      InputComponent: GridFilterInputValue
    }
  ];
};

export const customDateFilters = [
  ...getGridDateOperators().filter((filter) =>
    ["after", "before"].includes(filter.value)
  )
];

export const customNumberFilters = [
  ...getGridNumericOperators().filter(
    (filter) => !["isAnyOf", ">", "<"].includes(filter.value)
  )
];
